"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../molecules/Dialog";

import { Info, AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "../atoms/Button";
import { t } from "i18next";

interface TwoStepDeleteModalProps {
  /**
   * Props for the delete modal component
   * @param isOpen - Whether the modal is open or not
   */
  isOpen: boolean;
  /**
   * Function to close the modal
   */
  onClose: () => void;
  /**
   * Function to delete the item
   */
  onDelete: () => Promise<void>;
  /**
   * Name of the item to be deleted
   */
  itemName: string;
  /**
   * Function to be called when the user clicks the "Next" button
   */
  onNext?: () => void;

  /**
   * Text to be displayed in the info step
   */
  infoText?: string;
  /**
   * Text to be displayed in the delete step
   */
  deleteText?: string;
}

export function DeleteModal({
  isOpen,
  onClose,
  onDelete,
  itemName,
  infoText,
  deleteText,
  onNext,
}: TwoStepDeleteModalProps) {
  const [step, setStep] = useState<"info" | "confirm" | "deleting">("info");
  const [isDeleting, setIsDeleting] = useState(false);

  const handleNext = () => {
    if (onNext) onNext();
    setStep("confirm");
  };
  const handleBack = () => setStep("info");

  const handleClose = () => {
    onClose();
    setStep("info");
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    setStep("deleting");
    try {
      await onDelete();
      handleClose();
    } catch (error) {
      console.error("Error deleting item:", error);
      setIsDeleting(false);
      setStep("confirm");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <AnimatePresence mode="wait">
              {step === "info" ? (
                <motion.div
                  key="info-icon"
                  initial={{ rotate: 0 }}
                  exit={{ rotate: -180 }}
                  className="mr-2 p-2 bg-blue-100 rounded-full"
                >
                  <Info className="h-6 w-6 text-blue-500" />
                </motion.div>
              ) : step === "confirm" ? (
                <motion.div
                  key="alert-icon"
                  initial={{ rotate: 180 }}
                  animate={{ rotate: 0 }}
                  className="mr-2 p-2 bg-red-100 rounded-full"
                >
                  <AlertTriangle className="h-6 w-6 text-red-500" />
                </motion.div>
              ) : (
                <motion.div
                  key="loader-icon"
                  initial={{ rotate: 0 }}
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 1 }}
                  className="mr-2 p-2 bg-gray-100 rounded-full"
                >
                  <Loader2 className="h-6 w-6 text-gray-500" />
                </motion.div>
              )}
            </AnimatePresence>
            {t("general.delete")} {itemName}
          </DialogTitle>
        </DialogHeader>
        <AnimatePresence mode="wait">
          {step === "info" ? (
            <motion.div
              key="info"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
            >
              <p className="py-4">
                {infoText ?? t("modals.delete.info")} 
              </p>
              <DialogFooter>
                <Button onClick={handleClose}>{t("general.cancel")}</Button>
                <Button onClick={handleNext} className="bg-primary">
                  <span className="text-white">{t("general.next")}</span>
                </Button>
              </DialogFooter>
            </motion.div>
          ) : step === "confirm" ? (
            <motion.div
              key="confirm"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <p className="py-4">
                {deleteText ?? t("modals.delete.delete")}.
              </p>
              <DialogFooter>
                <Button onClick={handleBack}>{t("general.back")}</Button>
                <Button onClick={handleDelete}>{t("general.delete")}</Button>
              </DialogFooter>
            </motion.div>
          ) : (
            <motion.div
              key="deleting"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <p className="py-4">Deleting {itemName}...</p>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
